import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Settings, X, Bell, Loader2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const fmtHour = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"} ET`;

export const NotificationPrefs = ({ isAdmin }) => {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const p = user?.preferences || { digest_enabled: true, digest_hour: 8, instant_review_alert: false };
  const [prefs, setPrefs] = useState(p);
  const [profile, setProfile] = useState({ personal_email: "", whatsapp_number: "" });

  const openDialog = () => {
    setPrefs(user?.preferences || p);
    setProfile({ personal_email: user?.personal_email || "", whatsapp_number: user?.whatsapp_number || "" });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: prof } = await api.put("/me/profile", profile);
      let prefsData = null;
      if (isAdmin) { prefsData = (await api.put("/me/preferences", prefs)).data.preferences; }
      if (setUser) setUser((u) => ({
        ...u,
        personal_email: prof.personal_email,
        whatsapp_number: prof.whatsapp_number,
        ...(prefsData ? { preferences: prefsData } : {}),
      }));
      toast.success("Notification settings saved");
      setOpen(false);
    } catch { toast.error("Could not save settings"); }
    setSaving(false);
  };

  return (
    <>
      <button data-testid="notif-prefs-btn" onClick={openDialog} title="Notification settings"
        className="flex items-center justify-center rounded-md border border-white/15 h-9 w-9 text-slate-300 hover:bg-white/5 transition-colors">
        <Settings className="h-4 w-4" />
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div data-testid="notif-prefs-dialog" className="w-full max-w-md rounded-lg bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-600 flex items-center gap-2"><Bell className="h-4 w-4 text-teal" /> Notification Settings</h3>
              <button data-testid="notif-prefs-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-300"><X className="h-5 w-5" /></button>
            </div>

            {/* Profile / notification contacts (all users) */}
            <div className="rounded-md border border-white/10 p-3 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <UserIcon className="h-3.5 w-3.5 text-teal" />
                <span className="text-sm font-semibold text-slate-100">My Contact Details</span>
              </div>
              <p className="text-xs text-slate-400 mb-3">Used to notify you when an order is assigned to you. Order Number &amp; Product Type only.</p>
              <label className="text-xs font-semibold text-slate-300 mb-1 block">Personal Email</label>
              <input data-testid="profile-personal-email" type="email" value={profile.personal_email}
                onChange={(e) => setProfile({ ...profile, personal_email: e.target.value })}
                placeholder="you@personal.com"
                className="w-full mb-3 rounded-md border border-white/15 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" />
              <label className="text-xs font-semibold text-slate-300 mb-1 block">WhatsApp Number</label>
              <input data-testid="profile-whatsapp-number" value={profile.whatsapp_number}
                onChange={(e) => setProfile({ ...profile, whatsapp_number: e.target.value })}
                placeholder="+15551234567"
                className="w-full rounded-md border border-white/15 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-teal focus:outline-none" />
              <p className="text-[11px] text-slate-400 mt-1">Include country code (E.164), e.g. +1 555 123 4567.</p>
            </div>

            {isAdmin && (
              <>
                <label className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-sm text-slate-300">Daily digest email</span>
                  <input data-testid="pref-digest-enabled" type="checkbox" className="h-4 w-4 accent-teal"
                    checked={!!prefs.digest_enabled} onChange={(e) => setPrefs({ ...prefs, digest_enabled: e.target.checked })} />
                </label>

                <div className="py-3 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Digest delivery time</span>
                    <select data-testid="pref-digest-hour" disabled={!prefs.digest_enabled} value={prefs.digest_hour ?? 8}
                      onChange={(e) => setPrefs({ ...prefs, digest_hour: parseInt(e.target.value, 10) })}
                      className="rounded-md border border-white/15 px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal focus:outline-none disabled:bg-white/10">
                      {HOURS.map((h) => <option key={h} value={h}>{fmtHour(h)}</option>)}
                    </select>
                  </div>
                </div>

                <label className="flex items-center justify-between py-3">
                  <span className="text-sm text-slate-300 pr-4">Instant alert when a client reply needs review</span>
                  <input data-testid="pref-instant-alert" type="checkbox" className="h-4 w-4 accent-teal"
                    checked={!!prefs.instant_review_alert} onChange={(e) => setPrefs({ ...prefs, instant_review_alert: e.target.checked })} />
                </label>
              </>
            )}

            <div className="mt-5 flex justify-end">
              <button data-testid="notif-prefs-save" onClick={save} disabled={saving}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-navy-2 transition-colors disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save Settings
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
